import { JSX } from 'react';
import type {
  QuestionItem,
  ExistingSurveyFile,
} from 'src/redux/slices/Interview/types';
import BoolInput from './inputs/BoolInput';
import ChoicesInput from './inputs/ChoicesInput';
import MultiselectInput from './inputs/MultiselectInput';
import TextInput from './inputs/TextInput';
import TextAreaInput from './inputs/TextAreaInput';
import CurrencyInput from './inputs/CurrencyInput';
import NumberInput from './inputs/NumberInput';
import PercentageInput from './inputs/PercentageInput';
import DateInput from './inputs/DateInput';
import PhoneInput from './inputs/PhoneInput';
import EmailInput from './inputs/EmailInput';
import SSNInput from './inputs/SSNInput';
import LastSSNInput from './inputs/LastSSNInput';
import ZipCodeInput from './inputs/ZipCodeInput';
import AccountNumberInput from './inputs/AccountNumberInput';
import InterviewDocHub from './inputs/InterviewDocHub';

interface Props {
  question: QuestionItem;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  // File upload props (only needed for file questions)
  surveyId?: number;
  answerGroupId?: number | null;
  questionGroupId?: number;
  onAnswerGroupResolved?: (id: number) => void;
  certificationId?: number;
  onComplete?: (fileList?: ExistingSurveyFile[]) => void;
  onHasUploadsChange?: (count: number) => void;
  onReadyStateChange?: (state: { processing: boolean; ready: boolean }) => void;
  onContinueTrigger?: (fn: (() => void) | null) => void;
  /**
   * Session-local instance identity (e.g. `s0`, `s1`) — part of the React key
   * on InterviewDocHub so the upload context remounts fresh when moving
   * between instances of the same file QIT in a multi group. Uses instanceSeq
   * (not answerGroupId) so lazy answer-group creation mid-upload does not
   * remount the hub.
   */
  instanceKey?: string;
  /**
   * When true, the file hub skips its initial RenterFile fetch. Used for a
   * brand-new multi-group instance (answerGroupId still null): the unscoped
   * fetch would return the previous instances' files for the same QIT.
   */
  skipInitialFileFetch?: boolean;
  existingSurveyFiles?: ExistingSurveyFile[];
}

export default function QuestionInput({
  question,
  value,
  onChange,
  surveyId,
  answerGroupId,
  questionGroupId,
  onAnswerGroupResolved,
  certificationId,
  onComplete,
  onHasUploadsChange,
  onReadyStateChange,
  onContinueTrigger,
  instanceKey,
  skipInitialFileFetch,
  existingSurveyFiles,
}: Props): JSX.Element {
  // The backend serializes bool/choices/frequency answers as arrays (e.g. [1]),
  // the same format as multiselect. Unwrap to a scalar for single-choice inputs.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scalarValue = (v: any): number | null =>
    Array.isArray(v) ? v[0] ?? null : v;

  switch (question.question_type) {
    case 'bool':
      return (
        <BoolInput
          options={question.options}
          value={scalarValue(value)}
          onChange={onChange}
        />
      );
    case 'choices':
    case 'frequency':
      return (
        <ChoicesInput
          options={question.options}
          value={scalarValue(value)}
          onChange={onChange}
        />
      );
    case 'multiselect':
      return (
        <MultiselectInput
          options={question.options}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
        />
      );
    case 'text':
    case 'name':
    case 'eligibility_code':
      return <TextInput value={value ?? ''} onChange={onChange} />;
    case 'text_area':
      return <TextAreaInput value={value ?? ''} onChange={onChange} />;
    case 'currency':
      return <CurrencyInput value={value ?? ''} onChange={onChange} />;
    case 'number':
    case 'log_number':
      return <NumberInput value={value ?? ''} onChange={onChange} />;
    case 'percentage':
      return <PercentageInput value={value ?? ''} onChange={onChange} />;
    case 'date':
      return <DateInput value={value ?? ''} onChange={onChange} />;
    case 'phone':
      return <PhoneInput value={value ?? ''} onChange={onChange} />;
    case 'email':
      return <EmailInput value={value ?? ''} onChange={onChange} />;
    case 'ssn':
      return <SSNInput value={value ?? ''} onChange={onChange} />;
    case 'last_SSN':
      return <LastSSNInput value={value ?? ''} onChange={onChange} />;
    case 'zip_code':
      return <ZipCodeInput value={value ?? ''} onChange={onChange} />;
    case 'account_number':
      return <AccountNumberInput value={value ?? ''} onChange={onChange} />;
    case 'file':
      return (
        <InterviewDocHub
          key={`${question.qit_id}:${instanceKey ?? 's0'}`}
          surveyId={surveyId || 0}
          certificationId={certificationId}
          qitId={question.qit_id}
          answerGroupId={answerGroupId}
          questionGroupId={questionGroupId}
          maxFiles={question.number_of_files_required_per_answer}
          onComplete={onComplete ?? (() => undefined)}
          onHasUploadsChange={onHasUploadsChange}
          onAnswerGroupCreated={onAnswerGroupResolved}
          onReadyStateChange={onReadyStateChange}
          onContinueTrigger={onContinueTrigger}
          skipInitialFileFetch={skipInitialFileFetch}
          existingSurveyFiles={existingSurveyFiles}
        />
      );
    default:
      return <TextInput value={value ?? ''} onChange={onChange} />;
  }
}
